/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-lines */
import {useMemo, useCallback} from 'react';
import {isBoolean, isString, isArray, unionBy} from 'lodash-es';
import dayjs from 'dayjs';
import queryString from 'query-string';
import {useIntl} from 'react-intl';
import {RteMethodProps} from '@/rte';
import sdk from '@/sdk';
import * as store from '@/store';
import {SearchMember} from '@/interface';
import {MentionType, MentionFrom, MentionTypeNumber} from '@/constants/document/mention';
import {MemberType, EDITOR_MODE, ObjectType, URL_SOURCE, CITE_FROM, DocRole} from '@/Constant';
import {MentionNotifyType} from '@/constants/document/notification';
import {ICommentMentionNotify} from '@/types/document/notification';
import routers, {buildPath, ServerConfig} from '@/config/routers';
import {
    getHeadingUuidByUrl, getBlockIdByUrl, getRightCommentIdByUrl, getDocIdByUrl,
    isDocUrl, isDocShortUrl,
} from '@/utils/urlUtils';
import {dateFormat} from '@/utils/dateUtils';
import mentionCache from '@/utils/document/mentionCache';
import {concatImgSrc} from '@/utils/avatarUtils';
import {useCanAuthorizeRead, useAuthorizeRead} from '@/hooks/authorize';
import {
    reportQuickAuthorizePopper,
    reportQuickAuthorizeBtn,
    feLogger,
    searchScene,
    searchClickScene,
    clickMentionGroup,
} from '@/buried';
import {recordShowMentionDocObserver} from '@/buried/aigc';
import {curUserCanAuthorizeRead} from '@/store/util';
import {getIncrementMentionUserList} from '@/utils/document/mention';
import {scrollIntoBlockAndTips, scrollIntoComment, transformHyperlink} from '@/utils/document/common';
import {isHi, hiOpenChat} from '@/utils/browserUtils';
import {getDocKeyByType} from '@/utils/docType';
import toast, {ToastType} from '@/components/Toast';
import downCountToast from '@/components/DownCountToast';
import {authorizePopper, removeAuthorizePopper} from '@/domains/space/Repository/components/AuthorizePopper';
import {useSendRlNotify, useRecordNotNotify} from '@/hooks/common';
import {DOC_TYPE_KEY} from '@/constants/document/doc';
import DocSummaryCard from '@/domains/document/components/DocSummaryCard';
import {removeSearchParamNoRefresh, getUrlParams} from '@/utils/kuQuery';
import KU_QUERY from '@/constants/common/searchParamKeys';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';

import docSvg from '@/assets/svg/doc.svg';
import sheetSvg from '@/assets/svg/sheet.svg';
import pptSvg from '@/assets/svg/ppt.svg';
import datasheetSvg from '@/assets/svg/datasheet.svg';
import kuPiper from '@/middleware/kuPiper';
import {addSourceToUrl} from '@/buried/utils';
import {recognizeURL2TitleBySDK, renderSDKCard, flowObj, reportTextLinkClick} from '@/utils/document/flow';

import {UserInfo, User} from './interface';
import {useEditorUserInfoCard} from '../editorUserCard';
import styles from './index.module.scss';

const ObjectTypeDocTypeMap = {
    [ObjectType.Doc]: DOC_TYPE_KEY.DOCUMENT,
    [ObjectType.Sheet]: DOC_TYPE_KEY.SHEET,
    [ObjectType.PPT]: DOC_TYPE_KEY.PPT,
    [ObjectType.DATASHEET]: DOC_TYPE_KEY.DATASHEET,
};

const ObjectTypeSvgMap = {
    [ObjectType.Doc]: docSvg,
    [ObjectType.Sheet]: sheetSvg,
    [ObjectType.PPT]: pptSvg,
    [ObjectType.DATASHEET]: datasheetSvg,
};

const MentionErrorMsg = {
    NotNotify: 'Not Notify', // 未勾选通知
    Revoked: 'Revoked', // 撤销通知
    Removed: 'Removed', // ???场景未知
    ExcludeComment: 'Exclude Comment', // 排除评论
    Empty: 'Empty', // 空数据
};

/**
 * 判断消息是否为不需要通知的场景
 *
 * @param msg 消息内容
 * @returns 如果消息为不需要通知的场景，则返回 true；否则返回 false
 */
function isNotNotifyScene(msg: any) {
    return [MentionErrorMsg.NotNotify, MentionErrorMsg.Revoked].includes(msg);
}

type KeyOfMap = keyof typeof ObjectTypeDocTypeMap;

interface IMention {
    id: string;
    userId: string;
    username: string;
    mentionId?: string;
    email?: string;
}

interface UserData {
    name: string;
    icon: string;
    type: string;
    'icon1.5'?: string;
    'icon2'?: string;
    'icon3'?: string;
}

function recordMentionUser(mention: any, isToAddList: boolean) {
    if (isToAddList) {
        return;
    }

    if (mention.id) {
        sdk.as.postMentionRecord(mention.id, mention.type).catch(err => console.error(err));
    }
}

// 通知撤销的Toast
const closeToastAndResolveList: Array<() => void> = [];
function revokeNotifyToast(userNameList: string[], isCollect = false, mentionList?: any[]): Promise<boolean> {
    const duration = 5000;
    const nameStr = userNameList.join('、');
    let toastText = isCollect ? 'sendNotifyAfterPublish' : 'revokeRuliuMentionToast';
    // 区分只有@人的提示和包含群聊的提示
    if (mentionList && mentionList.length > 0) {
        const isSingleChat = mentionList.every(item => item.mentionType === 5);
        if (isSingleChat) {
            toastText = isCollect ? 'sendNotifyAfterPublish2' : 'revokeRuliuMentionToast2';
        }
    }

    const promise = new Promise<boolean>(resolve => {
        let toastObj: ReturnType<typeof downCountToast> | null = null;
        function revoke() {
            resolve(false);

            toastObj && toastObj.remove();
            toast({
                type: ToastType.Success,
                duration: 1500,
                content: <span>{kuPiper.openTextSlot('revokeRuliuMentionSuccessTip')}</span>,
            });
        }

        toastObj = downCountToast({
            duration: duration,
            content: (
                <span
                    className={styles.toastBox}
                >
                    {kuPiper.openTextSlot(toastText)}
                    <b className={styles.toastNameBox}>
                        （<span className={styles.toastName}>{nameStr}</span>）
                    </b>
                    <a onClick={revoke} style={{marginLeft: 10}}>
                        撤销
                    </a>
                </span>
            ),
            onClickDownCount: revoke,
            onClose: () => resolve(true),
        });

        function closeToastAndResolve() {
            resolve(true);
            toastObj && toastObj.remove();
        }

        closeToastAndResolveList.push(closeToastAndResolve);
    });

    return promise;
}
export function closeAllToastAndResolve() {
    closeToastAndResolveList.forEach(item => {
        item();
    });
}

let queryState: any = null;
// eslint-disable-next-line prefer-const
let userListState: any = [];
let docListState: any = [];
let groupListState: any = [];
const getMentionUserList = async (query: string, from: MentionFrom = MentionFrom.content, curUserGuid: string) => {
    try {
        let list = [];
        const result = await sdk.as.getMentionRecord({
            query,
        });
        list = result.users || [];

        const users = list.map((data: SearchMember) => {
            return {
                name: data.nickname,
                description: data.department,
                id: data.userGuid,
                subtitle: data.email,
                avatar: concatImgSrc(data.userGuid),
                isSelf: data.userGuid === curUserGuid,
                messageId: data.rlName,
                label: data.label,
                isVirtualUser: data.isVirtualUser,
            };
        });

        queryState = query;
        userListState = users;
        feLogger(
            searchScene({
                position: from === MentionFrom.content ? 'doc' : MentionFrom.comment,
            })
        );

        return users;
    } catch {
        return [];
    }
};

const openRlContacts = async (userInfo: UserInfo) => {
    let messageId = userInfo.messageId;
    if (!messageId) {
        const userId = userInfo.id;
        if (userId) {
            const result = await sdk.as.getUserInfoList([userId]);
            messageId = result[0].rlName;
        }
    }
    if (isHi()) {
        messageId && hiOpenChat(messageId);
    } else {
        messageId && (window.location.href = `baidu://message/?id=${messageId}`);
    }
};

const openRlPersonalHome = async (userInfo: UserInfo) => {
    if (userInfo) {
        const userId = userInfo.id;
        if (userId) {
            const result = await sdk.as.getUserInfoList([userId]);
            const user = result[0] || {};
            const path = buildPath(routers.PersonalHome.path, {
                userName: user?.avatar || '',
            });
            window.location.href = path || '';
        }
    }
};

const editorGetDocInfo = async (token: string, from?: string) => {
    const docQuery = token.split('$$');
    if (['icafe'].includes(docQuery[0])) {
        const urlArr = decodeURIComponent(docQuery[1]).split('/');
        const queryStr = urlArr[urlArr.length - 2];
        const queryArr = queryStr.split('-');
        const cardId = queryArr.pop();
        const spaceId = queryArr.join('-');
        const path = window.location.host === 'ku.dev.weiyun.baidu.com' ? 'kubak' : 'ku';
        const url = `${ServerConfig.origin}/wiki/${path}/icafe/card/${spaceId}/${cardId}`;
        try {
            const result = await sdk.serverBase.request({
                method: 'get',
                url,
                returnFullResult: true,
            });
            return {
                token,
                name: result.cards[0].title,
                id: `${new Date().getTime()}`,
                lastModify: result.cards[0].lastModifiedTime,
            };
        } catch (err) {
            return {
                errno: -1,
            };
        }
    }
    if (!['zsk'].includes(docQuery[0])) {
        return {
            errno: -1,
        };
    }
    try {
        const urls = [docQuery[1]];
        const getData = await sdk.serverBase.request({
            method: 'post',
            url: ServerConfig.origin + '/wiki/ku/util/identify' + (from ? `?from=${from}` : ''),
            data: {
                urls: urls,
                needSummary: true,
                userGuid: sdk.as.user?.userGuid,
            },
        });
        const result = getData[0];
        const timeStamp = Date.now();

        let docType = getDocKeyByType(result.type);

        if (result.sourceType === ObjectType.Repository) {
            docType = 'homepage';
        }
        let mentionNode = {
            id: `${timeStamp}`,
            name: result.name,
            lastModify: result.modified,
            token,
            docType,
        };
        if (result.docCardLink) {
            mentionNode = Object.assign({}, mentionNode, {
                previewUrl: result.docCardLink,
            });
        }

        // 重新监听
        recordShowMentionDocObserver.debounceObserve();

        return mentionNode;
    } catch (error) {
        return {
            errno: -1,
        };
    }
};

const checkIsTodoAutoTrans = (mention: any, options: any) => {
    // 若在正文(包含表格)中的待办中@人，不立即发通知，等发布时RTE会调用后端自动转换为如流待办
    return !!options.isFromTodoList;
};

const collectMention = kuPiper.runProxy(kuPiper.proxyNameMap.storeRlNotifyProxy, (docGuid, mentions, relation) => {
    return sdk.as.collectMention({
        docGuid,
        mentions,
        relation: relation || undefined,
    });
});

// 通知的Promise集合
const notifyHub = new Set<Promise<any>>();
export const getNotifyPromiseAll = () => {
    return Promise.all(notifyHub.values());
};
export const resetNotifyHub = () => {
    notifyHub.clear();
};

export const useMentionOptions = (
    editorRef: React.RefObject<RteMethodProps>,
    mode: EDITOR_MODE,
    isConvertedLink?: boolean
) => {
    const intl = useIntl();
    const userGuid = sdk.as.user?.userGuid;
    const isEditMode = mode === EDITOR_MODE.edit;
    const repository = store.getData(store.KEYS.CUR_REPOSITORY);
    const {docGuid} = store.getData(store.KEYS.CUR_DOCUMENT) || {};
    const canAuthorizeRead = useCanAuthorizeRead();
    const authorizeRead = useAuthorizeRead();

    const getUserList = useCallback(
        async (query: string, from = MentionFrom.content) => {
            const list = await getMentionUserList(query, from, userGuid!);
            return list;
        },
        [userGuid]
    );

    const getDocList = useCallback(
        async (query: string, from: MentionFrom = MentionFrom.content) => {
            try {
                let list = [];
                const result = await sdk.as.getMentionRecord({
                    type: 'doc',
                    query,
                });
                list = result.docs || [];

                list = list.map((doc: any) => {
                    const docPath = buildPath(routers.Knowledge.docPath, {
                        spaceGuid: doc.spaceGuid,
                        groupGuid: doc.groupGuid,
                        repositoryGuid: doc.repositoryGuid,
                        docGuid: doc.docGuid,
                    });

                    const docType =
                        ObjectTypeDocTypeMap[doc.objectType as KeyOfMap] || ObjectTypeDocTypeMap[ObjectType.Doc];

                    return {
                        id: doc.docGuid,
                        name: doc.name,
                        lastModify: dayjs(doc.modified).format('YYYY-MM-DD HH:mm:ss'),
                        token: `zsk$$${location.origin}${docPath}`,
                        subtitle: `最近更新于 ${dateFormat(doc.modified, intl)}`,
                        docType,
                        avatar: ObjectTypeSvgMap[doc.objectType as KeyOfMap] || ObjectTypeDocTypeMap[ObjectType.Doc],
                    };
                });
                queryState = query;
                docListState = list;
                feLogger(
                    searchScene({
                        position: from === MentionFrom.content ? 'doc' : MentionFrom.comment,
                    })
                );
                return list;
            } catch {
                return [];
            }
        },
        [intl]
    );

    const getGroupList = useCallback(
        async (query: string, from: MentionFrom = MentionFrom.content) => {
            try {
                let list = [];
                const result = await sdk.as.getMentionRecord({
                    type: 'group',
                    query,
                });
                list = result.groups || [];

                list = list.map((data: any) => {
                    return {
                        name: data.name,
                        id: data.gid,
                        subtitle: data.gid.toString(),
                        avatar: data.avatar,
                    };
                });
                queryState = query;
                groupListState = list;
                feLogger(
                    searchScene({
                        position: from === MentionFrom.content ? 'doc' : MentionFrom.comment,
                    })
                );
                return list;
            } catch {
                return [];
            }
        },
        []
    );

    const popAuthorizePopper = useMemo(() => {
        return async (mention: any, options: any) => {
            const {from = 'content', fromPaste = false} = options;
            if (fromPaste || !curUserCanAuthorizeRead()) {
                return;
            }

            const target = document.querySelector(`[data-mention-uuid="${mention.uuid}"]`) as HTMLElement;
            const mentionId = mention.id;

            if (target && mentionId) {
                const result = await canAuthorizeRead(repository, docGuid, mentionId);

                if (result) {
                    await authorizePopper({
                        repository,
                        docGuid,
                        editorMode: mode,
                        data: {
                            target,
                            from,
                        },
                    });
                }
            }
        };
    }, [canAuthorizeRead, repository, docGuid, mode]);

    const sendRlNotify = useSendRlNotify();
    const recordNotNotify = useRecordNotNotify();

    const path = buildPath(routers.Knowledge.docPath, {
        spaceGuid: repository.spaceGuid,
        groupGuid: repository.groupGuid,
        repositoryGuid: repository.repositoryGuid,
        docGuid: docGuid,
    });
    const docUrl = ServerConfig.origin + path;

    // 获取用户卡片的渲染函数
    const renderUserInfoCard = useEditorUserInfoCard({
        showPermissionBtn: true,
        docUrl: docUrl,
    });

    return useMemo(() => {
        return {
            // 统一链接识别能力开关
            enableRecognizeSDK: true,
            // 是否显示跳转如流会话按钮
            showMessageBtn: true,
            onMessageBtnClick: openRlContacts,
            // 是否显示跳转如流个人主页按钮
            showPersonalSiteBtn: true,
            onPersonalSiteBtnClick: openRlPersonalHome,
            getList: getUserList,
            getDocList: getDocList,
            getGroupList: getGroupList,
            renderUserInfoCard,
            renderDocInfoCard(mentionInfo: any, options: any) {
                const {visible, reRenderDocInfoCard} = options;
                if (!visible) {
                    return <div></div>;
                }

                if (mentionInfo.mentionType !== 'doc') {
                    return null;
                }

                // 历史数据中有 docType 不存在的情况，这种就当文档处理
                if (mentionInfo.docType === 'doc' || !mentionInfo.docType) {
                    const tokenUrl = mentionInfo.mentionToken;
                    let [, url] = tokenUrl.split('$$');
                    url = decodeURIComponent(url);
                    if (tokenUrl.startsWith('zsk') && url && url.startsWith('http')) {
                        return <DocSummaryCard
                            url={url}
                            updateCardPosition={reRenderDocInfoCard}
                        />;
                    }
                }

                return null;
            },
            async onMentionAdd(mentions: any = [], options: any = {}) {
                const mention = mentions[0];
                const createTime = +(new Date());
                if (mention) {
                    const MemberType: {[key in string]: string} = {contact: 'user', doc: 'com'};
                    const type: any = mention?.type || '';

                    if (type === MentionType.contact) {
                        const isNotNotify = checkIsTodoAutoTrans(mention, options);
                        if (isNotNotify) {
                            return;
                        }
                    }

                    const id: string = mention.id;
                    let sortIndex: any = '';
                    userListState.forEach((item: any, index: number) => {
                        if (item.id === id) {
                            sortIndex = index + 1;
                        }
                    });
                    docListState.forEach((item: any, index: number) => {
                        if (item.id === id) {
                            sortIndex = index + 1;
                        }
                    });
                    groupListState.forEach((item: any, index: number) => {
                        if (item.id === id) {
                            sortIndex = index + 1;
                        }
                    });
                    feLogger(
                        searchClickScene({
                            position: (options.from ? 'comment' : 'doc') + (queryState ? '' : '_last'),
                            sort: String(sortIndex),
                            type: type ? MemberType[type] : '',
                        })
                    );
                }

                if (
                    mention &&
                    (mention.type === MentionType.contact || mention.type === MentionType.group) &&
                    !options.fromPaste
                ) {
                    recordMentionUser(mention, !options.isToAddList);
                    if (mention.type === MentionType.group) {
                        feLogger(clickMentionGroup());
                    }
                }

                if (!mention || options.fromPaste) {
                    return;
                }

                mentionCache.add({
                    uuid: mention.uuid,
                    info: mention,
                    options,
                });

                // 如流待办需要弹框进行快捷授权
                if (mention.type === MentionType.contact && options.from === MentionFrom.onlineTodo) {
                    popAuthorizePopper(mention, options).catch(err => console.log(err));
                    return;
                }

                if (mention.type === MentionType.contact) {
                    // 排除自己
                    if (mention.id === userGuid) {
                        return;
                    }

                    const isCollectMention = isEditMode;
                    const mentions = [{
                        mentionId: mention.uuid,
                        userId: mention.id,
                        from: MentionNotifyType.content,
                        email: mention.subtitle,
                    }];

                    const promise = popAuthorizePopper(mention, options)
                        ?.then(() => {
                            let notRemoved = true;
                            try {
                                if (editorRef.current) {
                                    notRemoved = editorRef.current.getMentionList().some((item: any) => {
                                        return item.uuid === mention.uuid;
                                    });
                                }
                            } catch (err) {
                                console.error(err);

                                notRemoved = true;
                            }

                            // 此处只处理正文部分的提及通知，评论部分不在这里处理
                            // 勾选了则发送通知（包括单聊卡片通知、公众号通知、站内通知），没勾选则不发送通知
                            if (notRemoved && options.from !== MentionFrom.comment && options.checked) {
                                // 目前只有@人
                                const curUser = {...mention, mentionType: 5};
                                return revokeNotifyToast([mention.name], isCollectMention, [curUser]);
                            }

                            if (!notRemoved) {
                                return Promise.reject(new Error(MentionErrorMsg.Removed));
                            } else if (options.from === MentionFrom.comment) {
                                return Promise.reject(new Error(MentionErrorMsg.ExcludeComment));
                            } else {
                                return Promise.reject(new Error(MentionErrorMsg.NotNotify));
                            }
                        })
                        .then(async (isSend: boolean) => {
                            if (!isSend) {
                                return Promise.reject(new Error(MentionErrorMsg.Revoked));
                            }

                            if (isCollectMention) {
                                return collectMention(docGuid, mentions, undefined, undefined, createTime);
                            }

                            return sendRlNotify(
                                mentions,
                                {
                                    notifyType: 3,
                                    needVersion: isEditMode,
                                },
                                {
                                    spaceGuid: repository.spaceGuid,
                                    groupGuid: repository.groupGuid,
                                    repositoryGuid: repository.repositoryGuid,
                                    docGuid,
                                }
                            );
                        })
                        .catch(err => {
                            if (isNotNotifyScene(err?.message)) {
                                recordNotNotify(
                                    mentions,
                                    {
                                        notifyType: 3,
                                        needVersion: isEditMode,
                                        isCollectMention,
                                    },
                                    {
                                        spaceGuid: repository.spaceGuid,
                                        groupGuid: repository.groupGuid,
                                        repositoryGuid: repository.repositoryGuid,
                                        docGuid,
                                    }
                                );
                            }
                            console.log(err);
                        });

                    notifyHub.add(promise);
                }
            },
            getUserPermission: async (user: User, options: {from?: string} = {}) => {
                const result = {
                    reading: true,
                };
                if (await canAuthorizeRead(repository, docGuid, user.userId)) {
                    result.reading = false;
                }

                reportQuickAuthorizePopper({
                    accuser_id: userGuid,
                    page_id: docGuid,
                    kb_id: repository.repositoryGuid,
                    source: options.from === 'comment' ? 1 : 0,
                    suser_id: user.userId,
                    type: 1,
                });

                return result;
            },
            onRequirePermission: (user: UserInfo, type: any, options: {from?: string} = {}) => {
                reportQuickAuthorizeBtn({
                    accuser_id: userGuid,
                    page_id: docGuid,
                    kb_id: repository.repositoryGuid,
                    source: options.from === 'comment' ? 1 : 0,
                    suser_id: user.id,
                    type: 1,
                });

                return authorizeRead(repository, docGuid, [
                    {
                        nickname: user.name,
                        memberType: MemberType.User,
                        memberGuid: user.id,
                    },
                ], DocRole.Reader);
            },
            onDropdownListVisibilityChange: (visible: boolean) => {
                if (visible) {
                    removeAuthorizePopper();
                }
            },
            setMentionCheckboxStatus(status: boolean) {
                const user = sdk.as.user;

                if (user) {
                    user.settings.sendNotify = status;
                    sdk.as.updateUser(user).catch(err => console.error(err));
                }
            },
            getMentionPanelConfig() {
                let sendNotify = sdk.user?.settings.sendNotify;
                if (!isBoolean(sendNotify)) {
                    sendNotify = true;
                }

                return {
                    showCheckbox: true,
                    defaultChecked: sendNotify,
                    checkboxText: kuPiper.openTextSlot(
                        isEditMode ? 'mentionCheckboxTip2' : 'mentionCheckboxTip'
                    ), // @人通知调整为文档发布时发送 小流量下线
                    emptySearchText: '暂无历史记录',
                    docsTabName: '文档',
                };
            },
            // TODO: 这个方法目前会被调用两次，待编辑器修复，这里先做兼容
            getMentionUuidByUrl() {
                const mentionIdVal = getUrlParams(KU_QUERY.document.mentionId);
                let mentionId: string = '';
                if (isString(mentionIdVal)) {
                    mentionId = mentionIdVal;
                } else if (isArray(mentionIdVal)) {
                    mentionId = mentionIdVal[0];
                }

                if (mentionId) {
                    // bottom_开头的 mentionId 是底部评论@人通知的场景，正文和划线评论这里对这个场景进行屏蔽
                    if (mentionId.startsWith('bottom_')) {
                        return null;
                    }

                    // 延时是为了保证编辑器第二次调用时还能取到参数
                    window.setTimeout(() => {
                        removeSearchParamNoRefresh(KU_QUERY.document.mentionId);
                        removeSearchParamNoRefresh(KU_QUERY.document.mentionVersion);
                    }, 2000);
                    return mentionId.split(',');
                }

                return null;
            },
            mentionHasDeleted() {
                const mentionVersion = getUrlParams(KU_QUERY.document.mentionVersion);
                let msg = '此条提及信息已被删除';

                if (mentionVersion) {
                    const mentionVersionNum = Number(mentionVersion) || 0;
                    const {version} = store.getData(store.KEYS.CUR_DOCUMENT);

                    if (mentionVersionNum >= Number(version)) {
                        msg = '@内容还未发布，可点击“编辑”或等待发布后查看';
                    }

                    removeSearchParamNoRefresh(KU_QUERY.document.mentionVersion);
                }

                setTimeout(() => {
                    toast({
                        duration: 6000,
                        type: ToastType.Error,
                        content: msg,
                    });
                }, 600);
            },
            getDocInfo: editorGetDocInfo,
            recognizeURL2TitleBySDK,
            renderSDKCard,
            autoUpdateMentionDocTitle: true,
            getMentionDocTitleByURLs: async (urls: string[], from?: string) => {
                if (urls.length === 0) {
                    return;
                }
                const icafeArr = urls.filter(url => url.split('$$')[0] === 'icafe');
                const zskArr = urls.filter(url => url.split('$$')[0] === 'zsk');
                const icafeRequestArr = icafeArr.map(url => editorGetDocInfo(url));
                const zskRequestPromise = async (zskArr: string[]) => {
                    const url = ServerConfig.origin + '/wiki/ku/util/identify' + (from ? '?from=' + from : '');
                    const param = {
                        urls: zskArr.map(token => token.split('$$')[1]),
                    };
                    const result = await sdk.serverBase.request({
                        method: 'post',
                        url,
                        data: param,
                    });
                    return result;
                };
                return Promise.all([zskRequestPromise(zskArr), ...icafeRequestArr]).then(res => {
                    const [zskResArr, ...icafeResArr] = res;
                    const url2title: any = {};
                    zskResArr.length && zskResArr.forEach((el: any) => {
                        const key = 'zsk$$' + el.url;
                        url2title[key] = el?.name;
                    });
                    icafeResArr.length && icafeResArr.forEach((el: any) => {
                        url2title[el?.token] = el?.name;
                    });
                    return url2title;
                });
            },
            onMentionClick: (type: string, token: string, docType?: string, options?: {from : string}) => {
                if (type === 'doc' && token) {
                    // Note：增加点击 @sdk 文字链的埋点 so-kldoc-12880
                    const [prefix, urlSplit] = token.split('$$');
                    if (prefix === 'sdk') {
                        const tokenUrl = decodeURIComponent(urlSplit);
                        reportTextLinkClick(tokenUrl);
                    }

                    const tokenSplit = token.split('$$');
                    if (tokenSplit.length === 2) {
                        // 锚点链接地址
                        const url = decodeURIComponent(tokenSplit[1]);
                        if (url.startsWith('http')) {
                            const {from} = options || {};
                            const isDoc = isDocUrl(url) || isDocShortUrl(url);
                            const docUrlSource = from === CITE_FROM.content ?
                                URL_SOURCE.docContentMentionDoc : URL_SOURCE.underlineCommentMentionDoc;
                            const sourceUrl = addSourceToUrl(url, isDoc, {source: docUrlSource});

                            if (mode === EDITOR_MODE.version) {
                                window.open(sourceUrl);
                                return;
                            }

                            const blockId = getBlockIdByUrl(url);
                            const headingUuid = getHeadingUuidByUrl(url);
                            const tokenDocId = getDocIdByUrl(url);
                            // 判断是否为同一页面并且有锚点
                            if ((blockId || headingUuid) && tokenDocId === docGuid) {
                                // 滚动锚点至视窗内
                                scrollIntoBlockAndTips({blockId, headingUuid});
                                return;
                            }

                            // 带划线评论id的链接滚动到评论处
                            const commentId = getRightCommentIdByUrl(url);
                            if (commentId && tokenDocId === docGuid) {
                                scrollIntoComment(commentId);
                                return;
                            }
                            const newUrl = isConvertedLink ? transformHyperlink(url) : url;
                            window.open(newUrl);
                        }
                    }
                }
            },
        };
    }, [
        getUserList,
        getDocList,
        getGroupList,
        renderUserInfoCard,
        popAuthorizePopper,
        userGuid,
        editorRef,
        sendRlNotify,
        recordNotNotify,
        mode,
        repository,
        docGuid,
        canAuthorizeRead,
        authorizeRead,
        isEditMode,
    ]);
};

export const useDefaultMentionOptions = () => {
    const userGuid = sdk.as.user?.userGuid;

    const getUserList = useCallback(
        async (query: string, from = MentionFrom.content) => {
            const list = await getMentionUserList(query, from, userGuid!);
            return list;
        },
        [userGuid]
    );

    // 获取用户卡片的渲染函数
    const renderUserInfoCard = useEditorUserInfoCard({
        showPermissionBtn: false,
    });

    return useMemo(() => {
        return {
            showMessageBtn: true,
            renderUserInfoCard,
            onMessageBtnClick: openRlContacts,
            // 是否显示跳转如流个人主页按钮
            showPersonalSiteBtn: true,
            onPersonalSiteBtnClick: openRlPersonalHome,
            getList: getUserList,
            renderSDKCard,
        };
    }, [getUserList, renderUserInfoCard]);
};

// 底部评论使用的 mention 配置
export const useCommentMentionOptions = (
    editorRef: React.RefObject<RteMethodProps>,
    mode: EDITOR_MODE
) => {
    const mentionOptions = useMentionOptions(editorRef, mode);
    mentionOptions.renderDocInfoCard = () => null;

    const userGuid = sdk.as.user?.userGuid;
    const repository = store.getData(store.KEYS.CUR_REPOSITORY);
    const {docGuid} = store.getData(store.KEYS.CUR_DOCUMENT) || {};
    const canAuthorizeRead = useCanAuthorizeRead();
    const popAuthorizePopper = useMemo(() => {
        return async (mention: any, options: any) => {
            const {from = 'content', fromPaste = false} = options;
            if (fromPaste || !curUserCanAuthorizeRead()) {
                return;
            }

            const target = document.querySelector(`[data-mention-uuid="${mention.uuid}"]`) as HTMLElement;
            const mentionId = mention.id;

            if (target && mentionId) {
                const result = await canAuthorizeRead(repository, docGuid, mentionId);

                if (result) {
                    await authorizePopper({
                        repository,
                        docGuid,
                        editorMode: mode,
                        data: {
                            target,
                            from,
                        },
                    });
                }
            }
        };
    }, [canAuthorizeRead, repository, docGuid, mode]);

    const path = buildPath(routers.Knowledge.docPath, {
        spaceGuid: repository.spaceGuid,
        groupGuid: repository.groupGuid,
        repositoryGuid: repository.repositoryGuid,
        docGuid: docGuid,
    });
    const docUrl = ServerConfig.origin + path;

    // 获取用户卡片的渲染函数
    const renderUserInfoCard = useEditorUserInfoCard({
        showPermissionBtn: true,
        docUrl: docUrl,
    });

    return {
        ...mentionOptions,
        renderUserInfoCard,
        onMentionAdd(mentions: any = [], options: any = {}) {
            const mention = mentions[0];
            if (mention) {
                const MemberType: {[key in string]: string} = {contact: 'user', doc: 'com'};
                const type: any = mention?.type || '';
                const id: string = mention.id;
                let sortIndex: any = '';
                userListState.forEach((item: any, index: number) => {
                    if (item.id === id) {
                        sortIndex = index + 1;
                    }
                });
                docListState.forEach((item: any, index: number) => {
                    if (item.id === id) {
                        sortIndex = index + 1;
                    }
                });
                groupListState.forEach((item: any, index: number) => {
                    if (item.id === id) {
                        sortIndex = index + 1;
                    }
                });
                feLogger(
                    searchClickScene({
                        position: (options.from ? 'comment' : 'doc') + (queryState ? '' : '_last'),
                        sort: String(sortIndex),
                        type: type ? MemberType[type] : '',
                    })
                );
            }

            if (!mention || options.fromPaste) {
                return;
            }

            mentionCache.add({
                uuid: mention.uuid,
                info: mention,
                options,
            });

            if (mention.type === MentionType.contact) {
                recordMentionUser(mention, options.fromPaste);

                // 排除自己
                if (mention.id === userGuid) {
                    return;
                }

                popAuthorizePopper(mention, options);
            }
        },
        onMentionClick: (type: string, token: string) => {
            if (type === 'doc' && token) {
                // Note：增加点击 @sdk 文字链的埋点 so-kldoc-12880
                const [prefix, urlSplit] = token.split('$$');
                if (prefix === 'sdk') {
                    const tokenUrl = decodeURIComponent(urlSplit);
                    reportTextLinkClick(tokenUrl);
                }

                const tokenSplit = token.split('$$');
                if (tokenSplit.length === 2) {
                    // 锚点链接地址
                    const tokenUrl = decodeURIComponent(tokenSplit[1]);
                    if (tokenUrl.startsWith('http')) {
                        const blockId = getBlockIdByUrl(tokenUrl);
                        const headingUuid = getHeadingUuidByUrl(tokenUrl);
                        // 获取文档id
                        const tokenDocId = getDocIdByUrl(tokenUrl);
                        // 判断是否为同一页面并且有锚点
                        if ((blockId || headingUuid) && tokenDocId === docGuid) {
                            // 滚动锚点至视窗内
                            scrollIntoBlockAndTips({blockId, headingUuid});
                        } else {
                            const isDoc = isDocUrl(tokenUrl) || isDocShortUrl(tokenUrl);
                            const url = addSourceToUrl(tokenUrl, isDoc, {source: URL_SOURCE.bottomCommentMentionDoc});
                            const newUrl = transformHyperlink(url);
                            window.open(newUrl);
                        }
                    }
                }
            }
        },
        docMatches: [
            {
                prefix: 'zsk',
                // eslint-disable-next-line max-len
                match: /(http:\/\/)?((rlwiki.dev.weiyun.baidu.com:8902)?(rlkb-sandbox.dev.weiyun.baidu.com)?(wikigray.baidu-int.com)?(ku.baidu-int.com)?)\/knowledge\/(([A-Za-z0-9_-]+)(\/)?)+/,
            },
            {
                prefix: 'icafe',
                // eslint-disable-next-line max-len
                match: /https?:\/\/console.cloud(-sandbox)?.baidu-int.com\/devops\/icafe\/issue\/([A-Za-z0-9_-]+)\/show/,
            },
            {
                prefix: 'zsk',
                // eslint-disable-next-line max-len
                match: /https?:\/\/(ku.baidu-int.com)|(rlwiki.dev.weiyun.baidu.com:8902)|(rlkb-sandbox.dev.weiyun.baidu.com)|(wikigray.baidu-int.com)|(ku.dev.weiyun.baidu.com)|(ku-qa.dev.weiyun.baidu.com)|(ku-qa3.dev.weiyun.baidu.com)|(ku-qa4.dev.weiyun.baidu.com)\/d\/([A-Za-z0-9_-]+)/,
            },
        ],
        getDocInfo: editorGetDocInfo,
        disableRecognizeSDK: () => {
            return !flowObj.isRecognizeSupported();
        },
        renderSDKCard: null,
    };
};

// 划词评论的@人通知
export const useMentionNotifyOfComment = (mode: EDITOR_MODE, repository: Repository, docGuid: string) => {
    const sendRlNotify = useSendRlNotify();
    const recordNotNotify = useRecordNotNotify();

    return useCallback(
        async (data: ICommentMentionNotify) => {
            const {mentionList = [], content, curCommentId} = data.data || {};
            const createTime = +(new Date());
            interface IMention {
                subtitle: any;
                id: string;
                userId?: string;
                gid?: string;
                username: string;
                mentionType: number;
            }
            const checkedMentions: IMention[] = [];
            const uncheckedMentions: IMention[] = [];

            // 给人还是给群发送通知
            const isSendGroup = data.data.notifyType?.includes('group');
            const isSendContact = data.data.notifyType?.includes('contact');
            mentionList.filter(item => !item.isVirtualUser).forEach(({uuid}: {uuid: string}) => {
                const data = mentionCache.get(uuid);

                if (
                    data &&
                    !data.options.fromPaste &&
                    data.info.id !== sdk.as.user!.userGuid
                ) {
                    // @人
                    if (
                        data.info.type === MentionType.contact &&
                        isSendContact
                    ) {
                        const mention = {
                            id: data.info.uuid,
                            userId: data.info.id,
                            username: data.info.name,
                            mentionType: MentionTypeNumber.contact,
                            email: data.info.subtitle,
                        };

                        if (data.options.checked) {
                            checkedMentions.push(mention);
                        } else {
                            uncheckedMentions.push(mention);
                        }
                    }

                    // @群组
                    if (
                        data.info.type === MentionType.group &&
                        isSendGroup
                    ) {
                        checkedMentions.push({
                            id: data.info.uuid,
                            gid: data.info.id,
                            username: data.info.name,
                            mentionType: MentionTypeNumber.group,
                        });
                    }
                }
            });

            const docInfo = {
                spaceGuid: repository.spaceGuid,
                groupGuid: repository.groupGuid,
                repositoryGuid: repository.repositoryGuid,
                docGuid,
            };
            const isCollectMention = mode === EDITOR_MODE.edit;
            const checkedMentionList = checkedMentions.map(item => ({
                mentionId: item.id,
                userId: item.userId,
                from: MentionNotifyType.comment,
                mentionType: item.mentionType,
                gid: item.gid,
                name: item.username,
                email: item.email,
            }));

            const promise = Promise.resolve()
                .then(() => {
                    if (checkedMentions.length > 0) {
                        const nameList = unionBy(checkedMentions, 'userId').map(item => item.username);
                        const mList = unionBy(checkedMentions, 'userId');
                        return revokeNotifyToast(nameList, isCollectMention, mList);
                    }

                    return Promise.reject(new Error(MentionErrorMsg.Empty));
                })
                .then(async (isSend: boolean) => {
                    if (!isSend) {
                        return Promise.reject(new Error(MentionErrorMsg.Revoked));
                    }

                    if (isCollectMention) {
                        return collectMention(docGuid, checkedMentionList, {
                            type: 'rightComment',
                            id: curCommentId,
                        }, content, createTime);
                    }

                    return sendRlNotify(
                        checkedMentionList,
                        {
                            notifyType: 3,
                            message: content,
                            needVersion: false,
                        },
                        docInfo,
                        {
                            type: 'rightComment',
                            id: curCommentId,
                            docGuid,
                        },
                        createTime
                    );
                })
                .catch(err => {
                    if (isNotNotifyScene(err?.message)) {
                        recordNotNotify(
                            checkedMentionList,
                            {
                                notifyType: 3,
                                message: content,
                                needVersion: false,
                                isCollectMention,
                            },
                            docInfo,
                            {
                                type: 'rightComment',
                                id: curCommentId,
                                docGuid,
                            }
                        );
                    }
                    console.log(err);
                });

            notifyHub.add(promise);

            const uncheckedPromise = Promise.resolve()
                .then(() => {
                    const uncheckedMentionList = uncheckedMentions.map(item => ({
                        mentionId: item.id,
                        userId: item.userId,
                        from: MentionNotifyType.comment,
                        mentionType: item.mentionType,
                        gid: item.gid,
                        name: item.username,
                        email: item.subtitle,
                    }));

                    recordNotNotify(
                        uncheckedMentionList,
                        {
                            notifyType: 3,
                            message: content,
                            needVersion: false,
                            isCollectMention,
                        },
                        docInfo,
                        {
                            type: 'rightComment',
                            id: curCommentId,
                            docGuid,
                        }
                    );
                });
            notifyHub.add(uncheckedPromise);
        },
        [sendRlNotify, recordNotNotify, mode, repository, docGuid]
    );
};

// 发布逻辑处理移到后端处理，暂时注释保留
// export const useMentionNotifyOfPublish = (repository: Repository, docGuid: string) => {
//     const sendRlNotify = useSendRlNotify();

//     return useCallback(
//         (editor: RteMethodProps) => {
//             if (!editor) {
//                 return;
//             }
//             const mentions = editor ? getIncrementMentionUserList(editor) : [];

//             if (mentions.length > 0) {
//                 sendRlNotify(
//                     mentions.map(item => {
//                         const result: any = {
//                             mentionId: item.uuid,
//                             userId: item.info.id,
//                             from: item.options.from === MentionFrom.comment ? 1 : 2,
//                         };

//                         if (result.from === 1) {
//                             result.commentId = mentionCache.getCommentId(item.uuid);
//                         }

//                         return result;
//                     }),
//                     {
//                         notifyType: 2,
//                     },
//                     {
//                         spaceGuid: repository.spaceGuid,
//                         groupGuid: repository.groupGuid,
//                         repositoryGuid: repository.repositoryGuid,
//                         docGuid,
//                     }
//                 );
//             }
//         },
//         [sendRlNotify, repository, docGuid]
//     );
// };

export const useMentionNotifyOfBottomComment = () => {
    const sendRlNotify = useSendRlNotify();
    const recordNotNotify = useRecordNotNotify();

    return useCallback((
        mentionList: Array<{
            uuid: string;
            mentionId: string;
        }>,
        message: string = '',
        commentId: string
    ) => {
        try {
            const repository = store.getData(store.KEYS.CUR_REPOSITORY);
            const {docGuid} = store.getData(store.KEYS.CUR_DOCUMENT) || {};
            const docInfo = {
                spaceGuid: repository.spaceGuid,
                groupGuid: repository.groupGuid,
                repositoryGuid: repository.repositoryGuid,
                docGuid,
            };

            const checkedMentions: IMention[] = [];
            const uncheckedMentions: IMention[] = [];

            mentionList.forEach(({uuid, mentionId}: {uuid: string, mentionId: string}) => {
                const data = mentionCache.get(uuid);

                if (
                    data &&
                    data.info.type === MentionType.contact &&
                    !data.options.fromPaste &&
                    data.info.id !== sdk.as.user!.userGuid
                ) {
                    const mention = {
                        id: data.info.uuid,
                        userId: data.info.id,
                        username: data.info.name,
                        mentionId,
                    };
                    if (data.options.checked) {
                        checkedMentions.push(mention);
                    } else {
                        uncheckedMentions.push(mention);
                    }
                }
            });

            Promise.resolve()
                .then(() => {
                    if (checkedMentions.length > 0) {
                        const nameList = unionBy(checkedMentions, 'userId').map(item => item.username);
                        // 目前只有@人
                        const mList = unionBy(checkedMentions, 'userId').map((item) => {
                            return {
                                ...item,
                                mentionType: 5,
                            };
                        });
                        return revokeNotifyToast(nameList, false, mList);
                    }

                    if (uncheckedMentions.length > 0) {
                        return Promise.reject(new Error(MentionErrorMsg.NotNotify));
                    }

                    return Promise.reject(new Error(MentionErrorMsg.Empty));
                })
                .then((isSend: boolean) => {
                    if (!isSend) {
                        return Promise.reject(new Error(MentionErrorMsg.Revoked));
                    }

                    sendRlNotify(
                        checkedMentions.map(item => ({
                            mentionId: item.mentionId as string,
                            userId: item.userId,
                            from: MentionNotifyType.bottom_comment,
                        })),
                        {
                            notifyType: 3,
                            message,
                            needVersion: false,
                        },
                        docInfo,
                        {
                            type: 'bottomComment',
                            id: commentId,
                            docGuid,
                        }
                    );

                    recordNotNotify(
                        uncheckedMentions.map(item => ({
                            mentionId: item.mentionId as string,
                            userId: item.userId,
                            from: MentionNotifyType.bottom_comment,
                        })),
                        {
                            notifyType: 3,
                            message,
                            needVersion: false,
                        },
                        docInfo,
                        {
                            type: 'bottomComment',
                            id: commentId,
                            docGuid,
                        }
                    );
                })
                .catch(err => {
                    if (isNotNotifyScene(err?.message)) {
                        recordNotNotify(
                            [...checkedMentions, ...uncheckedMentions].map(item => ({
                                mentionId: item.mentionId as string,
                                userId: item.userId,
                                from: MentionNotifyType.bottom_comment,
                            })),
                            {
                                notifyType: 3,
                                message,
                                needVersion: false,
                            },
                            docInfo,
                            {
                                type: 'bottomComment',
                                id: commentId,
                                docGuid,
                            }
                        );
                    }
                    console.log(err);
                });
        } catch (err) {
            console.error(err);
        }
    }, [sendRlNotify, recordNotNotify]);
};
